// ==========================================
// AuthCallback.jsx — V22 DEFINITIVO
// ✅ detectSessionInUrl: true no supabase.js
// ✅ Supabase processa hash automaticamente
// ✅ AuthCallback só aguarda e pega a sessão
// ==========================================
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Loader2, Truck, Building2, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processando login...');
  const [user, setUser] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [loadingTipo, setLoadingTipo] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [error, setError] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const handleCallback = async () => {
      try {
        console.log('🚀 [AuthCallback] INICIANDO...');
        setStatus('Processando login...');

        // ✅ PASSO 1: Aguarda o Supabase processar o hash automaticamente
        // O detectSessionInUrl: true já extraiu o access_token do hash
        console.log('⏳ [AuthCallback] Aguardando Supabase processar hash...');
        await new Promise(r => setTimeout(r, 1500));

        // ✅ PASSO 2: Pega a sessão que o Supabase já criou
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('❌ [AuthCallback] Erro sessão:', sessionError);
          setError('Erro de sessão: ' + sessionError.message);
          setTimeout(() => navigate('/login?error=session'), 3000);
          return;
        }

        if (!session?.user) {
          console.error('❌ [AuthCallback] Sem sessão após processamento');
          setError('Não foi possível completar o login. Tente novamente.');
          setTimeout(() => navigate('/login?error=no_session'), 3000);
          return;
        }

        console.log('✅ [AuthCallback] Sessão ativa:', session.user.id);

        // ✅ PASSO 3: Limpar URL para não reprocessar
        window.history.replaceState({}, document.title, '/auth/callback');

        // ✅ PASSO 4: Verificar perfil
        await checkProfile(session.user);

      } catch (err) {
        console.error('❌ [AuthCallback] Erro geral:', err);
        setError('Erro inesperado. Tente novamente.');
        setTimeout(() => navigate('/login?error=exception'), 3000);
      }
    };

    const checkProfile = async (authUser) => {
      console.log('🔍 [AuthCallback] Verificando perfil:', authUser.id);
      setStatus('Verificando perfil...');
      setUser(authUser);

      try {
        const { data: perfilData, error: perfilError } = await supabase
          .from('profiles')
          .select('tipo, nome, email')
          .eq('id', authUser.id)
          .maybeSingle();

        if (perfilError) {
          console.error('⚠️ [AuthCallback] Erro perfil:', perfilError);
        }

        if (!perfilData) {
          console.log('➡️ [AuthCallback] Criando perfil...');

          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: authUser.id,
              email: authUser.email,
              nome: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'Usuário',
              tipo: null,
              role: 'user',
              status: 'em_analise',
              created_at: new Date().toISOString()
            });

          if (insertError) {
            console.error('❌ [AuthCallback] Erro criar perfil:', insertError);
            setError('Erro ao criar perfil: ' + insertError.message);
            setStatus('selecionar-tipo');
            return;
          }

          console.log('✅ [AuthCallback] Perfil criado!');
          setPerfil(null);
          setStatus('selecionar-tipo');
          return;
        }

        console.log('✅ [AuthCallback] Perfil:', perfilData);
        setPerfil(perfilData);

        if (perfilData?.tipo) {
          redirectByTipo(authUser.id, perfilData.tipo);
        } else {
          setStatus('selecionar-tipo');
        }

      } catch (err) {
        console.error('❌ [AuthCallback] Erro:', err);
        setStatus('selecionar-tipo');
      }
    };

    const redirectByTipo = async (userId, tipo) => {
      console.log('🔄 [AuthCallback] Redirecionando:', tipo);

      if (tipo === 'empresa') {
        const { data: empresa } = await supabase
          .from('empresas')
          .select('cnpj')
          .eq('usuario_id', userId)
          .maybeSingle();
        navigate(empresa?.cnpj ? '/perfil' : '/cadastro-empresa');
        return;
      }

      if (tipo === 'motorista') {
        const { data: motorista } = await supabase
          .from('motoristas')
          .select('cpf')
          .eq('usuario_id', userId)
          .maybeSingle();
        navigate(motorista?.cpf ? '/painel' : '/cadastro-motorista');
        return;
      }

      if (tipo === 'admin') {
        await supabase.auth.signOut();
        navigate('/admin/login?info=use_admin_login');
        return;
      }

      navigate('/selecionar-tipo');
    };

    handleCallback();
  }, [navigate]);

  const handleSelecionarTipo = async (tipo) => {
    if (!user?.id) {
      setError('Erro: usuário não identificado.');
      return;
    }

    setLoadingTipo(tipo);
    setError('');

    try {
      const safeTipo = tipo.toLowerCase().trim();
      const nome = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário';

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ tipo: safeTipo, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw new Error('Erro ao salvar tipo: ' + updateError.message);

      if (safeTipo === 'empresa') {
        await supabase.from('empresas').upsert({
          usuario_id: user.id,
          razao_social: nome,
          email: user.email,
          status: 'em_analise',
          updated_at: new Date().toISOString()
        }, { onConflict: 'usuario_id' });
      } else {
        await supabase.rpc('criar_motorista', {
          p_usuario_id: user.id,
          p_nome_completo: nome,
          p_email: user.email
        });
      }

      window.location.href = safeTipo === 'motorista' ? '/cadastro-motorista' : '/cadastro-empresa';

    } catch (err) {
      console.error('❌ [AuthCallback] Erro:', err);
      setError(err.message);
      setLoadingTipo(null);
    }
  };

  if (status !== 'selecionar-tipo') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center px-4">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{status}</h2>
          {error && <p className="text-red-400 text-sm mt-2 bg-red-500/10 p-2 rounded">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-5xl w-full relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700 mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-slate-400 text-sm">Escolha seu perfil</span>
          </div>

          <h1 className="text-5xl font-bold text-white mb-4 tracking-tight">
            Bem-vindo ao <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">BoraFrete</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Escolha como você quer usar a plataforma e comece a transformar seus negócios
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-center backdrop-blur-sm">
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={() => handleSelecionarTipo('motorista')}
            onMouseEnter={() => setHovered('motorista')}
            onMouseLeave={() => setHovered(null)}
            disabled={loadingTipo !== null}
            className={`group relative bg-slate-800/40 border rounded-2xl p-8 transition-all duration-500 text-left
              ${hovered === 'motorista' ? 'border-amber-400/50 bg-slate-800/60 scale-[1.02]' : 'border-slate-700/50'}
              ${loadingTipo === 'motorista' ? 'opacity-70' : 'hover:border-amber-400/30'}
              disabled:cursor-not-allowed`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-start justify-between mb-6">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300
                  ${hovered === 'motorista' ? 'bg-amber-500/20' : 'bg-amber-500/10'}`}>
                  <Truck className={`w-8 h-8 transition-all duration-300
                    ${hovered === 'motorista' ? 'text-amber-400 scale-110' : 'text-amber-400/70'}`} />
                </div>
                {hovered === 'motorista' && <ArrowRight className="w-6 h-6 text-amber-400 animate-bounce" />}
              </div>

              <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-amber-100 transition-colors">
                Sou Motorista
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Encontre cargas disponíveis, faça fretes e aumente sua renda. Conecte-se com empresas que precisam de transporte.
              </p>

              <div className="mt-auto">
                <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                  <img src="/motorista123.jpg" alt="Motorista"
                    className={`w-full h-full object-cover transition-all duration-500
                      ${hovered === 'motorista' ? 'scale-110' : 'scale-100'}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                </div>
                <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Cadastro gratuito</span>
                </div>
              </div>

              {loadingTipo === 'motorista' && (
                <div className="absolute inset-0 bg-slate-900/80 rounded-2xl flex flex-col items-center justify-center backdrop-blur-sm">
                  <Loader2 className="w-10 h-10 text-amber-400 animate-spin mb-3" />
                  <span className="text-amber-400 font-medium">Configurando perfil...</span>
                </div>
              )}
            </div>
          </button>

          <button
            onClick={() => handleSelecionarTipo('empresa')}
            onMouseEnter={() => setHovered('empresa')}
            onMouseLeave={() => setHovered(null)}
            disabled={loadingTipo !== null}
            className={`group relative bg-slate-800/40 border rounded-2xl p-8 transition-all duration-500 text-left
              ${hovered === 'empresa' ? 'border-emerald-400/50 bg-slate-800/60 scale-[1.02]' : 'border-slate-700/50'}
              ${loadingTipo === 'empresa' ? 'opacity-70' : 'hover:border-emerald-400/30'}
              disabled:cursor-not-allowed`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-start justify-between mb-6">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300
                  ${hovered === 'empresa' ? 'bg-emerald-500/20' : 'bg-emerald-500/10'}`}>
                  <Building2 className={`w-8 h-8 transition-all duration-300
                    ${hovered === 'empresa' ? 'text-emerald-400 scale-110' : 'text-emerald-400/70'}`} />
                </div>
                {hovered === 'empresa' && <ArrowRight className="w-6 h-6 text-emerald-400 animate-bounce" />}
              </div>

              <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-emerald-100 transition-colors">
                Sou Empresa
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Cadastre suas cargas e encontre motoristas confiáveis. Gestão completa de fretes e transportes.
              </p>

              <div className="mt-auto">
                <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                  <img src="/empresa12.jpg" alt="Empresa"
                    className={`w-full h-full object-cover transition-all duration-500
                      ${hovered === 'empresa' ? 'scale-110' : 'scale-100'}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                </div>
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Publique cargas grátis</span>
                </div>
              </div>

              {loadingTipo === 'empresa' && (
                <div className="absolute inset-0 bg-slate-900/80 rounded-2xl flex flex-col items-center justify-center backdrop-blur-sm">
                  <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mb-3" />
                  <span className="text-emerald-400 font-medium">Configurando perfil...</span>
                </div>
              )}
            </div>
          </button>
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            Você pode alterar seu tipo de perfil depois nas configurações
          </p>
        </div>
      </div>
    </div>
  );
}